import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'
import { organizationSchema, websiteSchema } from '@/lib/structured-data'

const toAbsolute = (path?: string): string | undefined => {
    if (!path) return undefined
    return path.startsWith('http') ? path : `${siteConfig.url}${path}`
}

const renderJsonLd = (data: Record<string, unknown>, key: string): ReactNode => (
    <script
        key={key}
        type='application/ld+json'
        dangerouslySetInnerHTML={{
            __html: JSON.stringify({ '@context': 'https://schema.org', ...data })
        }}
    />
)

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage,
    ogImageAlt,
    canonical,
    type = 'website',
    keywords,
    noindex = false,
    publishedTime,
    modifiedTime,
    author,
    section,
    tags,
    jsonLd
}): ReactNode => {
    const pageTitle = title ? `${title} | ${siteConfig.name}` : `${siteConfig.name} | Whole-Person Wellness`

    // Facebook, LinkedIn and X will not render an SVG share card, so a vector
    // cover (the pillar guide artwork) falls back to the static brand card
    // rather than shipping an image none of them can display. The alt falls
    // back with it, since the caller's alt describes the vector, not the card.
    const isVector = Boolean(ogImage && ogImage.split('?')[0].toLowerCase().endsWith('.svg'))
    const cardImage = isVector ? siteConfig.ogImage : ogImage

    const image = toAbsolute(cardImage)
    const canonicalUrl = toAbsolute(canonical)
    const isBrandCard = Boolean(cardImage && cardImage.endsWith('/og.png'))
    const imageAlt = isVector ? pageTitle : (ogImageAlt ?? pageTitle)
    const locale = siteConfig.locale ?? 'en_US'

    // `follow` is kept even when noindexing: the nav-group directories exist to
    // pass crawlers through to the pillar pages, so nofollow would defeat their
    // only purpose. Admin screens set their own `noindex, nofollow` directly.
    const robots = noindex
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

    const pageGraphs = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta name='robots' content={robots} />
            <meta name='googlebot' content={robots} />
            {keywords && keywords.length > 0 && <meta name='keywords' content={keywords.join(', ')} />}
            {author && <meta name='author' content={author} />}
            {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}

            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content={type} />
            <meta property='og:locale' content={locale} />
            {canonicalUrl && <meta property='og:url' content={canonicalUrl} />}
            {image && <meta property='og:image' content={image} />}
            {image && <meta property='og:image:alt' content={imageAlt} />}
            {isBrandCard && <meta property='og:image:width' content='1344' />}
            {isBrandCard && <meta property='og:image:height' content='768' />}

            {type === 'article' && publishedTime && (
                <meta property='article:published_time' content={publishedTime} />
            )}
            {type === 'article' && modifiedTime && (
                <meta property='article:modified_time' content={modifiedTime} />
            )}
            {type === 'article' && author && <meta property='article:author' content={author} />}
            {type === 'article' && section && <meta property='article:section' content={section} />}
            {type === 'article' &&
                tags &&
                tags.map((tag) => <meta key={tag} property='article:tag' content={tag} />)}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />
            {image && <meta name='twitter:image' content={image} />}
            {image && <meta name='twitter:image:alt' content={imageAlt} />}

            {renderJsonLd(organizationSchema(), 'ld-organization')}
            {renderJsonLd(websiteSchema(), 'ld-website')}
            {pageGraphs.map((graph, index) => renderJsonLd(graph, `ld-page-${index}`))}
        </Head>
    )
}
